import React, { useCallback, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import {
  Box,
  Dialog,
  Button,
  Stack,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import {
  LinkOff as LinkOffIcon,
  AttachFile as AttachFileIcon,
} from "@mui/icons-material";

import WidgetOverlayRow from "./WidgetOverlayRow";
import SelectionHandles from "./SelectionHandles";
import { pdfRectToCanvasRect } from "../../../../../../../utils/coordinateUtils";
import { isPreviewGroupLabelRecord } from "../preview/previewFieldLabel";
import { getFieldSettings } from "../../../../../../../utils/fieldSettings";

const cleanPdfTextForLabel = (text = "") =>
  String(text || "")
    .replace(/[-_\\.:*]{3,}/g, " ")
    .replace(/^[-_\\.:*\s]+/, "")
    .replace(/[-_\\.:*\s]+$/, "")
    .replace(/\s+/g, " ")
    .trim();

const SMALL_TITLE_WORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "but",
  "by",
  "for",
  "in",
  "nor",
  "of",
  "on",
  "or",
  "the",
  "to",
  "up",
  "via",
  "with",
]);

const capitalizeWord = (word = "") =>
  String(word || "")
    .toLowerCase()
    .replace(/^([a-z])/, (match) => match.toUpperCase())
    .replace(/(['’])S\b/g, "$1s");

const toTitleCase = (text = "") =>
  String(text || "")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index, words) => {
      const normalizedWord = word.replace(/^[^a-z]+|[^a-z]+$/gi, "");
      const isSmallWord = SMALL_TITLE_WORDS.has(normalizedWord);
      const isEdgeWord = index === 0 || index === words.length - 1;

      return isSmallWord && !isEdgeWord ? word : capitalizeWord(word);
    })
    .join(" ");

const InsertedTextOverlayRow = React.memo(function InsertedTextOverlayRow({
  edit,
  canvasRect,
  displayScale,
  isSelected,
  isEditing,
  interactive,
  moveEnabled,
  onStartMove,
  onStartResize,
  highlightFields,
  isPreview,
  documentKey,
  setCurrentProcessingFile,
  pushHistory,
}) {
  const inputRef = React.useRef(null);
  const editingSessionRef = React.useRef(null);
  const [textDraft, setTextDraft] = useState(edit.new_text || "");
  const commitTimerRef = React.useRef(null);
  const maxWaitTimerRef = React.useRef(null);
  const commitDraftRef = React.useRef(null);
  const settings = edit.textSettings || {};
  const fontSize =
    (Number(settings.fontSize) > 0 ? Number(settings.fontSize) : 12) *
    displayScale;
  const fontFamilies = {
    Helvetica: "Arial, Helvetica, sans-serif",
    "Times-Roman": '"Times New Roman", Times, serif',
    Courier: '"Courier New", Courier, monospace',
  };
  const fontFamily =
    fontFamilies[settings.fontFamily] ||
    settings.fontFamily ||
    fontFamilies.Helvetica;

  const clearCommitTimers = useCallback(() => {
    clearTimeout(commitTimerRef.current);
    clearTimeout(maxWaitTimerRef.current);
    commitTimerRef.current = null;
    maxWaitTimerRef.current = null;
  }, []);

  React.useLayoutEffect(() => {
    const text = edit.new_text || "";
    const session = editingSessionRef.current;
    if (!isEditing) {
      clearCommitTimers();
      editingSessionRef.current = null;
      setTextDraft(text);
      return;
    }

    if (!session || session.documentKey !== documentKey) {
      clearCommitTimers();
      editingSessionRef.current = {
        documentKey,
        originalText: text,
        committedText: text,
        draftText: text,
        historyRecorded: false,
      };
      setTextDraft(text);
      const input = inputRef.current;
      if (input) {
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      }
    } else if (text !== session.committedText) {
      // A newer external edit wins over any pending local draft.
      clearCommitTimers();
      session.originalText = text;
      session.committedText = text;
      session.draftText = text;
      session.historyRecorded = false;
      setTextDraft(text);
    }
  }, [isEditing, edit.new_text, documentKey, clearCommitTimers]);

  React.useLayoutEffect(() => clearCommitTimers, [clearCommitTimers]);

  // Match only our insertion record; never alter extracted PDF text or widgets.
  const updateRecord = (update) => {
    setCurrentProcessingFile((previous) => {
      const temp = previous?.document?.tempFields;
      if (temp?.currentDocument?.base64File !== documentKey) return previous;
      const currentEdit = temp?.pdfTextEdits?.find(
        (candidate) =>
          candidate.edit_id === edit.edit_id &&
          candidate.target_item_id === edit.target_item_id &&
          candidate.page === edit.page &&
          candidate.isEditorAdded === true &&
          candidate.kind === "inserted" &&
          candidate.action !== "delete",
      );
      if (!currentEdit) return previous;

      const nextTemp = update(temp, currentEdit);
      if (nextTemp === temp) return previous;

      return {
        ...previous,
        document: { ...previous.document, tempFields: nextTemp },
      };
    });
  };

  const selectText = (startEditing = false) => {
    if (!interactive) return;

    updateRecord((temp, currentEdit) => ({
      ...temp,
      selectedFormField: null,
      selectedExtraFields: [],
      selectedPdfTextItem: {
        id: currentEdit.target_item_id,
        page: currentEdit.page,
        kind: "inserted",
        isEditorAdded: true,
        text: currentEdit.new_text || "",
        rect: { ...currentEdit.rect },
      },
      selectedExtraPdfTextItems: [],
      editingInsertedTextId: startEditing ? currentEdit.edit_id : null,
    }));
  };

  const commitDraft = () => {
    clearCommitTimers();
    const session = editingSessionRef.current;
    if (!session || session.draftText === session.committedText) return;
    const text = session.draftText;
    const previousText = session.committedText;
    session.committedText = text;

    updateRecord((temp, currentEdit) => {
      if (
        temp.editingInsertedTextId !== currentEdit.edit_id ||
        temp.selectedPdfTextItem?.id !== currentEdit.target_item_id ||
        (currentEdit.new_text || "") !== previousText
      ) {
        return temp;
      }

      return {
        ...temp,
        pdfTextEdits: temp.pdfTextEdits.map((candidate) =>
          candidate.edit_id === currentEdit.edit_id
            ? { ...currentEdit, new_text: text, action: "insert" }
            : candidate,
        ),
        selectedPdfTextItem: { ...temp.selectedPdfTextItem, text },
      };
    });
  };

  React.useLayoutEffect(() => {
    commitDraftRef.current = commitDraft;
  });

  React.useLayoutEffect(() => {
    if (!isEditing) return undefined;

    const flushPendingDraft = () => {
      const session = editingSessionRef.current;
      if (!session || session.draftText === session.committedText) return;
      // Publish before another control reads document state (Save, Undo, etc.).
      flushSync(() => commitDraftRef.current?.());
    };
    const flushBeforeOutsideInteraction = (event) => {
      if (event.target !== inputRef.current) flushPendingDraft();
    };
    const flushBeforeShortcut = (event) => {
      if (
        event.target !== inputRef.current ||
        ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s")
      ) {
        flushPendingDraft();
      }
    };

    window.addEventListener("pointerdown", flushBeforeOutsideInteraction, true);
    window.addEventListener("click", flushBeforeOutsideInteraction, true);
    window.addEventListener("focusin", flushBeforeOutsideInteraction, true);
    window.addEventListener("keydown", flushBeforeShortcut, true);
    window.addEventListener("blur", flushPendingDraft);
    return () => {
      window.removeEventListener(
        "pointerdown",
        flushBeforeOutsideInteraction,
        true,
      );
      window.removeEventListener("click", flushBeforeOutsideInteraction, true);
      window.removeEventListener(
        "focusin",
        flushBeforeOutsideInteraction,
        true,
      );
      window.removeEventListener("keydown", flushBeforeShortcut, true);
      window.removeEventListener("blur", flushPendingDraft);
    };
  }, [isEditing]);

  const changeText = (text) => {
    const session = editingSessionRef.current;
    if (!session || text === session.draftText) return;

    if (!session.historyRecorded) {
      pushHistory?.();
      session.historyRecorded = true;
    }

    session.draftText = text;
    setTextDraft(text);
    clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(() => commitDraftRef.current?.(), 250);
    if (maxWaitTimerRef.current === null) {
      maxWaitTimerRef.current = setTimeout(
        () => commitDraftRef.current?.(),
        1000,
      );
    }
  };

  const finishEditing = () => {
    commitDraft();
    updateRecord((temp, currentEdit) =>
      temp.editingInsertedTextId === currentEdit.edit_id
        ? { ...temp, editingInsertedTextId: null }
        : temp,
    );
  };

  const textStyle = {
    fontFamily,
    fontSize: `${fontSize}px`,
    fontWeight: settings.bold ? 700 : 400,
    fontStyle: settings.italic ? "italic" : "normal",
    lineHeight: 1.2,
    color: "#111",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  };

  return (
    <Box
      data-editor-added-text="true"
      data-text-edit-id={edit.edit_id}
      role={interactive && !isEditing ? "button" : undefined}
      tabIndex={interactive && !isEditing ? 0 : undefined}
      aria-label={
        isPreview
          ? undefined
          : moveEnabled
            ? "Added text. Drag to move or use handles to resize. Double-click or press Enter to edit."
            : "Added text. Double-click or press Enter to edit."
      }
      onMouseDown={(event) => {
        event.stopPropagation();
        // Let the textarea keep native caret placement and text selection.
        if (!interactive || isEditing || event.button !== 0 || !moveEnabled) {
          return;
        }

        event.preventDefault();
        event.currentTarget.focus({ preventScroll: true });
        selectText();
        // The second press belongs to double-click editing, not another drag.
        if (event.detail < 2) onStartMove(event, edit);
      }}
      onClick={(event) => {
        event.stopPropagation();
        if (!isEditing && !isSelected) selectText();
      }}
      onDoubleClick={(event) => {
        event.stopPropagation();
        if (!isEditing) selectText(true);
      }}
      onKeyDown={(event) => {
        if (!interactive || isEditing) return;
        if (event.key === "Enter" || event.key === "F2") {
          event.preventDefault();
          event.stopPropagation();
          selectText(true);
        }
      }}
      style={{
        left: canvasRect.left,
        top: canvasRect.top,
        width: canvasRect.width,
        height: canvasRect.height,
      }}
      sx={{
        ...textStyle,
        position: "absolute",
        zIndex: isEditing ? 105 : isSelected ? 102 : 35,
        boxSizing: "border-box",
        borderRadius: 0,
        outline: isPreview
          ? "none"
          : isSelected || isEditing
            ? "1px solid #1976d2"
            : highlightFields
              ? "1px dashed rgba(25,118,210,0.8)"
              : "none",
        outlineOffset: isPreview ? 0 : "1px",
        backgroundColor: isPreview
          ? "transparent"
          : isEditing
            ? "rgba(255,255,255,0.98)"
            : isSelected
              ? "rgba(25,118,210,0.04)"
              : "transparent",
        boxShadow: isEditing ? "0 0 0 3px rgba(25,118,210,0.18)" : "none",
        cursor: !interactive
          ? "default"
          : isEditing || !moveEnabled
            ? "text"
            : "move",
        pointerEvents: interactive ? "auto" : "none",
        userSelect: isEditing ? "text" : "none",
        ...(interactive && {
          "&:hover": {
            outline: "1px solid #1976d2",
            backgroundColor: isEditing
              ? "rgba(255,255,255,0.98)"
              : "rgba(25,118,210,0.06)",
          },
          "&:hover::before, &:focus-visible::before": {
            opacity: 1,
          },
          "&:focus-visible": {
            outline: "1px solid #1976d2",
            boxShadow: "0 0 0 3px rgba(25,118,210,0.18)",
          },
        }),
        "&::before": {
          content: isPreview
            ? "none"
            : isEditing
              ? '"Editing text"'
              : '"Added text"',
          opacity: isSelected || isEditing ? 1 : 0,
          position: "absolute",
          bottom: "calc(100% + 2px)",
          left: "-2px",
          backgroundColor: "#1976d2",
          color: "#fff",
          font: "600 10px/16px Arial, sans-serif",
          letterSpacing: "0.2px",
          padding: "0 6px",
          borderRadius: 0,
          boxShadow: "none",
          whiteSpace: "nowrap",
          pointerEvents: "none",
        },
      }}
    >
      {isEditing ? (
        <Box
          component="textarea"
          ref={inputRef}
          rows={1}
          aria-label="Edit added text"
          placeholder="Type text"
          value={textDraft}
          onChange={(event) => changeText(event.target.value)}
          onBlur={finishEditing}
          onKeyDown={(event) => {
            // Let the existing document Save shortcut run after capture flushes.
            if (
              (event.ctrlKey || event.metaKey) &&
              event.key.toLowerCase() === "s"
            ) {
              return;
            }
            event.stopPropagation();
            if (event.nativeEvent.isComposing) return;

            if (event.key === "Escape") {
              event.preventDefault();
              changeText(editingSessionRef.current?.originalText ?? "");
              event.currentTarget.blur();
            } else if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              event.currentTarget.blur();
            }
          }}
          sx={{
            ...textStyle,
            display: "block",
            width: "100%",
            height: "100%",
            minHeight: 0,
            boxSizing: "border-box",
            resize: "none",
            overflow: "auto",
            border: 0,
            outline: "none",
            padding: 0,
            margin: 0,
            borderRadius: 0,
            backgroundColor: "transparent",
            cursor: "text",
            "&::placeholder": { color: "#64748b", opacity: 1 },
            userSelect: "text",
          }}
        />
      ) : (
        <Box sx={{ width: "100%", height: "100%", overflow: "hidden" }}>
          {edit.new_text ||
            (!isPreview && <span style={{ color: "#64748b" }}>Type text</span>)}
        </Box>
      )}
      {isSelected && moveEnabled && !isEditing && !isPreview && (
        <Box
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            pointerEvents: "none",
            "& > *": {
              pointerEvents: "auto",
              backgroundColor: "#fff",
              borderColor: "#1976d2",
            },
            "& *": {
              borderRadius: "0 !important",
              boxShadow: "none !important",
            },
          }}
        >
          <SelectionHandles
            onHandleMouseDown={(event, position) => {
              event.stopPropagation();
              if (event.button !== 0) return;
              event.preventDefault();
              selectText();
              onStartResize(event, edit, position);
            }}
          />
        </Box>
      )}
    </Box>
  );
});

const FormFieldOverlayLayer = ({
  currentProcessingFile: committedProcessingFile,
  widgetMovePreviewRef,
  pageNumber,
  scale,
  selectToolActive,
  typingToolActive,
  highlightFields,
  textOverlayDisplayMode,
  focusedField,
  setFocusedField,
  currCursor,
  unassignedFieldNames,
  fixedInputs,
  inputRef,
  childInputRef,
  pdfContainerRef,
  setCurrentProcessingFile,
  setStartPoint,
  setRect,
  setIsDrawing,
  setCurrCursor,
  setFreeSelectStartPoint,
  setMoving,
  setIsSelecting,
  setInitialMousePosition,
  setInitialInputSize,
  setInputPosition,
  setSelectionRect,
  setResizing,
  setResizeDirection,
  handleMouseDown,
  handleFieldChange,
  undoRedoStats,
  activeEditorTool,
  isPreview = false,
  baseViewPort,
  fieldList,
  onNavigateToTaggedField,
  formFields,
  onPreviewFieldClick,
  previewHoveredFieldNames = [],
  onFavouriteDrop,
  favouriteDropDisabled = false,
}) => {
  const [movePreviewFile, setMovePreviewFile] = useState(null);
  React.useImperativeHandle(
    widgetMovePreviewRef,
    () => ({
      setFile: setMovePreviewFile,
    }),
    [],
  );
  const currentProcessingFile = movePreviewFile || committedProcessingFile;

  const pageHeight =
    currentProcessingFile?.document?.tempFields?.currentDocument
      ?.actualHeight || 792;
  const viewport =
    currentProcessingFile?.document?.tempFields?.currentDocument?.viewport ||
    null;
  const pageXMin =
    currentProcessingFile?.document?.tempFields?.currentDocument?.pageXMin || 0;
  const pageYMin =
    currentProcessingFile?.document?.tempFields?.currentDocument?.pageYMin || 0;
  const scaleFactor =
    currentProcessingFile?.document?.tempFields?.currentDocument?.scaleFactor ||
    1;

  const [openEditBlockDialog, setOpenEditBlockDialog] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [deletedFields, setDeletedFields] = useState([]);

  const handleTaggedFieldNavigation = (event, field) => {
    if (!event.altKey || !field?.field_id) {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();
    onNavigateToTaggedField?.(field);
    return true;
  };

  const buildLabelFromSelectedPdfText = React.useCallback((tempFields) => {
    const primaryText = cleanPdfTextForLabel(
      tempFields?.selectedPdfTextItem?.text || "",
    );
    const extraTexts = (tempFields?.selectedExtraPdfTextItems || [])
      .map((item) => cleanPdfTextForLabel(item?.text || ""))
      .filter(Boolean);

    return toTitleCase(
      [primaryText, ...extraTexts]
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim(),
    );
  }, []);

  const sameRect = (a, b) =>
    !!a && !!b && a.X === b.X && a.Y === b.Y && a.W === b.W && a.H === b.H;
  const createFieldMatcher = (target, allFields) => {
    if (!target) return () => false;

    if (target.field_instance_id) {
      return (f) =>
        !!f?.field_instance_id &&
        f.field_instance_id === target.field_instance_id;
    }

    if (target.name) {
      const count = (allFields || []).filter(
        (f) => f?.name === target.name,
      ).length;
      if (count === 1) return (f) => f?.name === target.name;
    }

    if (target.field_id && target.page != null && target.order != null) {
      const matches = (allFields || []).filter(
        (f) =>
          f?.field_id === target.field_id &&
          f?.page === target.page &&
          f?.order === target.order,
      );
      if (matches.length === 1) {
        return (f) =>
          f?.field_id === target.field_id &&
          f?.page === target.page &&
          f?.order === target.order;
      }
    }

    if (target.field_id && target.rect) {
      const matches = (allFields || []).filter(
        (f) =>
          f?.field_id === target.field_id && sameRect(f?.rect, target.rect),
      );
      if (matches.length === 1) {
        return (f) =>
          f?.field_id === target.field_id && sameRect(f?.rect, target.rect);
      }
    }

    if (target.field_id) {
      const matches = (allFields || []).filter(
        (f) => f?.field_id === target.field_id,
      );
      if (matches.length === 1) {
        return (f) => f?.field_id === target.field_id;
      }
    }
    return () => false;
  };

  const assignLabelFromSelectedPdfText = React.useCallback(
    (prevFile, field) => {
      const tempFields = prevFile?.document?.tempFields || {};
      const nextLabel = buildLabelFromSelectedPdfText(tempFields);

      if (!nextLabel || !field) {
        return prevFile;
      }

      return {
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...tempFields,
            formFields: (() => {
              const allFields = tempFields.formFields || [];
              const isMatchingField = createFieldMatcher(field, allFields);
              return allFields.map((existingField) =>
                isMatchingField(existingField)
                  ? {
                      ...existingField,
                      field_label: nextLabel,
                      label: nextLabel,
                    }
                  : existingField,
              );
            })(),
            selectedFormField: null,
            selectedExtraFields: [],
            selectedPdfTextItem: null,
            selectedExtraPdfTextItems: [],
          },
        },
      };
    },
    [buildLabelFromSelectedPdfText],
  );

  const handleCloseEditBlockDialog = () => {
    setOpenEditBlockDialog(false);
    setSelectedBlock(null);
  };

  const pickRectComponent = (rect = {}, upper, lower, fallback = 0) => {
    const value = rect?.[upper] ?? rect?.[lower] ?? fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : Number(fallback) || 0;
  };

  const getBoundingBox = useCallback(
    (fields, padding = 8) => {
      const currentDocument =
        currentProcessingFile?.document?.tempFields?.currentDocument || {};

      const minY = Math.min(...fields.map((f) => f.y));
      const viewport = currentDocument?.viewport || null;

      const topField = fields?.length
        ? fields.reduce((maxItem, item) =>
            item.y < maxItem.y ? item : maxItem,
          )
        : {};

      const y = pickRectComponent(topField, "Y", "y", 0);

      const maxY = Math.max(...fields.map((f) => f.y + f.height));
      return {
        x: 20,
        y: y - 3,
        width: viewport?.width - 50,
        height: (maxY - minY + padding * 0.5) * scaleFactor,
      };
    },
    [currentProcessingFile?.document?.tempFields?.currentDocument, scaleFactor],
  );

  const handleEditConfirm = (isAll) => {
    const tempFields = currentProcessingFile?.document?.tempFields;

    const allSelectedField =
      isAll ||
      tempFields?.formFields?.filter(
        (f) => f?.block_id === selectedBlock && !f?.name?.startsWith("block__"),
      )?.length === deletedFields?.length;

    const updatedFormFields = tempFields?.formFields?.map((f) => {
      if (
        allSelectedField &&
        f?.block_id === selectedBlock &&
        !f?.name?.startsWith("block__")
      ) {
        return {
          ...f,
          block_id: "",
          block_label: "",
        };
      } else if (deletedFields?.includes(f?.name)) {
        return {
          ...f,
          block_id: "",
          block_label: "",
        };
      } else {
        return f;
      }
    });

    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: allSelectedField
            ? updatedFormFields?.filter((f) => f?.block_id !== selectedBlock)
            : updatedFormFields?.map((f) => {
                if (
                  f?.block_id === selectedBlock &&
                  f?.name?.startsWith("block__")
                ) {
                  const bb = getBoundingBox(
                    updatedFormFields?.filter(
                      (f) =>
                        f?.block_id === selectedBlock &&
                        !f?.name?.startsWith("block__"),
                    ),
                    8,
                  );

                  return {
                    ...f,
                    y: bb?.y ?? f?.y,
                    height: bb?.height ?? f?.height,
                  };
                } else {
                  return f;
                }
              }),
        },
      },
    }));

    setOpenEditBlockDialog(false);
    setSelectedBlock(null);
    setDeletedFields([]);
  };

  const rowEventContextRef = React.useRef({
    currentProcessingFile,
    setCurrentProcessingFile,
    onPreviewFieldClick,
    onFavouriteDrop,
    handleTaggedFieldNavigation,
    handleMouseDown,
    setStartPoint,
    setRect,
    setIsDrawing,
    setCurrCursor,
    setFreeSelectStartPoint,
    setMoving,
    setIsSelecting,
    setInitialMousePosition,
    setInitialInputSize,
    setInputPosition,
    setSelectionRect,
    setResizing,
    setResizeDirection,
    setFocusedField,
    handleFieldChange,
    assignLabelFromSelectedPdfText,
  });
  React.useLayoutEffect(() => {
    rowEventContextRef.current = {
      currentProcessingFile,
      setCurrentProcessingFile,
      onPreviewFieldClick,
      onFavouriteDrop,
      handleTaggedFieldNavigation,
      handleMouseDown,
      setStartPoint,
      setRect,
      setIsDrawing,
      setCurrCursor,
      setFreeSelectStartPoint,
      setMoving,
      setIsSelecting,
      setInitialMousePosition,
      setInitialInputSize,
      setInputPosition,
      setSelectionRect,
      setResizing,
      setResizeDirection,
      setFocusedField,
      handleFieldChange,
      assignLabelFromSelectedPdfText,
    };
  });
  const updateFileForRow = useCallback(
    (...args) => rowEventContextRef.current.setCurrentProcessingFile(...args),
    [],
  );
  const handleFavouriteDrop = useCallback(
    (...args) => rowEventContextRef.current.onFavouriteDrop?.(...args),
    [],
  );

  const fieldListById = useMemo(() => {
    const result = new Map();
    for (const field of fieldList || []) {
      if (!result.has(field?.field_id)) result.set(field?.field_id, field);
    }
    return result;
  }, [fieldList]);

  const previewGroupSettingsById = useMemo(() => {
    const result = new Map();

    if (!isPreview) return result;

    const widgets =
      currentProcessingFile?.document?.tempFields?.formFields || [];

    widgets.forEach((field) => {
      const groupType =
        fieldListById.get(field?.field_id)?.field_type ?? field?.field_type;

      if (!isPreviewGroupLabelRecord(field, groupType)) return;

      result.set(field.field_id, getFieldSettings(field));
    });

    return result;
  }, [
    isPreview,
    currentProcessingFile?.document?.tempFields?.formFields,
    fieldListById,
  ]);

  const formFieldsById = useMemo(() => {
    const result = new Map();
    for (const field of formFields || []) {
      if (!result.has(field?.field_id)) result.set(field?.field_id, field);
    }
    return result;
  }, [formFields]);
  const unassignedNames = useMemo(
    () =>
      new Set(Array.isArray(unassignedFieldNames) ? unassignedFieldNames : []),
    [unassignedFieldNames],
  );
  const hoveredNames = useMemo(
    () => new Set(previewHoveredFieldNames),
    [previewHoveredFieldNames],
  );
  const rectCache = useMemo(
    () => new WeakMap(),
    [isPreview, baseViewPort, viewport, pageHeight, scale, pageXMin, pageYMin],
  );
  const getCanvasRect = (field) => {
    let rect = rectCache.get(field);
    if (!rect) {
      rect = pdfRectToCanvasRect(
        field,
        isPreview ? baseViewPort : viewport || pageHeight,
        scale,
        pageXMin,
        pageYMin,
      );
      rectCache.set(field, rect);
    }
    return rect;
  };

  const tempFields = currentProcessingFile?.document?.tempFields || {};
  const selectedFieldNames = new Set(
    [tempFields.selectedFormField, ...(tempFields.selectedExtraFields || [])]
      .filter(Boolean)
      .map((field) => field.name),
  );
  const selectedFieldsOnPage = (tempFields.formFields || []).filter((field) => {
    if (!selectedFieldNames.has(field.name) || field?.hidden) return false;

    const rawPage = field?.page_num ?? field?.page;
    const parsedPage = Number(rawPage);
    return Number.isFinite(parsedPage) ? parsedPage === pageNumber : true;
  });
  const selectedCanvasRects = selectedFieldsOnPage.map(getCanvasRect);
  const groupSelectionRect =
    selectedCanvasRects.length > 1
      ? {
          left: Math.min(...selectedCanvasRects.map((rect) => rect.left)),
          top: Math.min(...selectedCanvasRects.map((rect) => rect.top)),
          width:
            Math.max(
              ...selectedCanvasRects.map((rect) => rect.left + rect.width),
            ) - Math.min(...selectedCanvasRects.map((rect) => rect.left)),
          height:
            Math.max(
              ...selectedCanvasRects.map((rect) => rect.top + rect.height),
            ) - Math.min(...selectedCanvasRects.map((rect) => rect.top)),
        }
      : null;
  const groupResizeAnchorField =
    selectedFieldsOnPage.find(
      (field) => field.name === tempFields.selectedFormField?.name,
    ) || selectedFieldsOnPage[0];

  const insertedTextInteractive =
    !isPreview &&
    (selectToolActive ||
      activeEditorTool === "insertText" ||
      tempFields.formFieldTools?.insertText?.active === true);
  const insertedTextMoveEnabled =
    !isPreview && selectToolActive && activeEditorTool === "edit";
  const handleInsertedTextMoveStart = useCallback((event, edit) => {
    const context = rowEventContextRef.current;
    const currentEdit =
      context.currentProcessingFile?.document?.tempFields?.pdfTextEdits?.find(
        (candidate) =>
          candidate.edit_id === edit.edit_id &&
          candidate.isEditorAdded === true &&
          candidate.kind === "inserted" &&
          candidate.action !== "delete",
      );
    if (!currentEdit?.rect) return;

    context.setResizing(false);
    context.setIsDrawing(false);
    context.setRect(null);
    context.setIsSelecting(false);
    context.setSelectionRect(null);
    context.setFreeSelectStartPoint(null);
    context.setFocusedField(null);
    context.setResizeDirection("move");
    // Identify this as a text gesture without placing text into formFields.
    context.setInitialMousePosition({
      x: event.clientX,
      y: event.clientY,
      insertedTextEditId: currentEdit.edit_id,
    });
    context.setInitialInputSize({
      width: currentEdit.rect.W,
      height: currentEdit.rect.H,
    });
    context.setInputPosition({
      x: currentEdit.rect.X,
      y: currentEdit.rect.Y,
    });
    context.setCurrCursor("move");
    context.setMoving(true);
  }, []);
  const handleInsertedTextResizeStart = useCallback(
    (event, edit, position) => {
      const context = rowEventContextRef.current;
      const temp = context.currentProcessingFile?.document?.tempFields;
      const currentEdit = temp?.pdfTextEdits?.find(
        (candidate) =>
          candidate.edit_id === edit.edit_id &&
          candidate.isEditorAdded === true &&
          candidate.kind === "inserted" &&
          candidate.action !== "delete" &&
          Number(candidate.page) === Number(pageNumber),
      );
      if (
        !currentEdit?.rect ||
        temp.selectedPdfTextItem?.id !== currentEdit.target_item_id ||
        temp.editingInsertedTextId
      ) {
        return;
      }

      const currentDocument = temp.currentDocument || {};
      const displayedRect = pdfRectToCanvasRect(
        currentEdit.rect,
        currentDocument.viewport || currentDocument.actualHeight || 792,
        currentDocument.scaleFactor || scale || 1,
        currentDocument.pageXMin || 0,
        currentDocument.pageYMin || 0,
      );
      context.setMoving(false);
      context.setIsDrawing(false);
      context.setRect(null);
      context.setIsSelecting(false);
      context.setSelectionRect(null);
      context.setFreeSelectStartPoint(null);
      context.setFocusedField(null);
      context.setResizeDirection(position.direction);
      context.setInitialMousePosition({
        x: event.clientX,
        y: event.clientY,
        insertedTextEditId: currentEdit.edit_id,
      });
      // Resize helpers use displayed canvas geometry, unlike move's PDF origin.
      context.setInitialInputSize({
        width: displayedRect.width,
        height: displayedRect.height,
      });
      context.setInputPosition({ x: displayedRect.left, y: displayedRect.top });
      context.setCurrCursor(position.cursor);
      context.setResizing(true);
    },
    [pageNumber, scale],
  );
  const textViewport = isPreview ? baseViewPort : viewport;
  const textDisplayScale =
    (textViewport?.transform
      ? Math.hypot(textViewport.transform[0], textViewport.transform[1])
      : Number(scale)) || 1;
  const insertedTextEdits = (tempFields.pdfTextEdits || []).filter(
    (edit) =>
      edit?.isEditorAdded === true &&
      edit.kind === "inserted" &&
      edit.edit_id &&
      edit.rect &&
      edit.action !== "delete" &&
      Number(edit.page) === Number(pageNumber),
  );

  return (
    <>
      {groupSelectionRect &&
        groupResizeAnchorField &&
        selectToolActive &&
        !isPreview && (
          <Box
            sx={{
              position: "absolute",
              left: `${groupSelectionRect.left}px`,
              top: `${groupSelectionRect.top}px`,
              width: `${groupSelectionRect.width}px`,
              height: `${groupSelectionRect.height}px`,
              outline: "2px dotted blue",
              outlineOffset: "0px",
              boxSizing: "border-box",
              backgroundColor: "transparent",
              pointerEvents: "none",
              zIndex: 101,
              "& > *": { pointerEvents: "auto" },
            }}
          >
            <SelectionHandles
              onHandleMouseDown={(e, pos) => {
                e.stopPropagation();
                handleMouseDown(
                  e,
                  currentProcessingFile,
                  setCurrentProcessingFile,
                  groupResizeAnchorField,
                  scale,
                  pos.direction,
                  pos.cursor,
                  pdfContainerRef,
                  setStartPoint,
                  setRect,
                  setIsDrawing,
                  setCurrCursor,
                  setFreeSelectStartPoint,
                  setMoving,
                  setIsSelecting,
                  setInitialMousePosition,
                  setInitialInputSize,
                  setInputPosition,
                  setSelectionRect,
                  setResizing,
                  setResizeDirection,
                  pageNumber,
                );
              }}
            />
          </Box>
        )}

      {currentProcessingFile?.document?.tempFields?.formFields
        .filter((field) => {
          if (field?.hidden) {
            return false;
          }

          const rawPage = field?.page_num ?? field?.page;
          const parsedPage = Number(rawPage);
          return Number.isFinite(parsedPage) ? parsedPage === pageNumber : true;
        })
        .map((field, index) => {
          const mainRect = getCanvasRect(field);
          const checkboxRect = mainRect;

          const isPrimarySelected =
            currentProcessingFile?.document?.tempFields?.selectedFormField
              ?.name === field?.name;

          const isExtraSelected =
            !isPrimarySelected && selectedFieldNames.has(field.name);
          const isUnassigned =
            !field?.block_id ||
            !field?.field_id ||
            unassignedNames.has(field?.name);
          const definition = fieldListById.get(field?.field_id);
          const parentDefinition =
            definition?.field_type === "option" && definition?.parent_field_id
              ? fieldListById.get(definition.parent_field_id)
              : undefined;
          const markingDefinition = parentDefinition ?? definition;

          const instanceSettings = previewGroupSettingsById.get(
            markingDefinition?.field_id,
          );

          const markCircle = isPreview
            ? (instanceSettings?.mark_circle ?? markingDefinition?.mark_circle)
            : markingDefinition?.mark_circle;

          const shouldMarkCircle =
            Boolean(markCircle) && isPreview && field?.value === true;
          const isMultiline = definition?.field_type === "multiline";
          const isPreviewHovered = hoveredNames.has(field?.name);
          return (
            <WidgetOverlayRow
              key={field.name ?? index}
              field={field}
              index={index}
              eventContextRef={rowEventContextRef}
              mainRect={mainRect}
              checkboxRect={checkboxRect}
              isPrimarySelected={isPrimarySelected}
              isExtraSelected={isExtraSelected}
              groupSelectionRect={
                isPrimarySelected && Boolean(groupSelectionRect)
              }
              selectToolActive={selectToolActive}
              typingToolActive={typingToolActive}
              highlightFields={highlightFields}
              textOverlayDisplayMode={textOverlayDisplayMode}
              focusedField={focusedField === field.name ? focusedField : null}
              currCursor={currCursor}
              isUnassigned={isUnassigned}
              fixedInputs={fixedInputs}
              activeEditorTool={activeEditorTool}
              setCurrentProcessingFile={updateFileForRow}
              isPreview={isPreview}
              formField={formFieldsById.get(field?.field_id)}
              shouldMarkCircle={shouldMarkCircle}
              isMultiline={isMultiline}
              isPreviewHovered={isPreviewHovered}
              setOpenEditBlockDialog={setOpenEditBlockDialog}
              setSelectedBlock={setSelectedBlock}
              onFavouriteDrop={
                typeof onFavouriteDrop === "function"
                  ? handleFavouriteDrop
                  : onFavouriteDrop
              }
              favouriteDropDisabled={favouriteDropDisabled}
              inputRef={inputRef}
              childInputRef={childInputRef}
              pdfContainerRef={pdfContainerRef}
              scale={scale}
              pageNumber={pageNumber}
            />
          );
        })}

      {insertedTextEdits.map((edit) => {
        const isSelected =
          tempFields.selectedPdfTextItem?.id === edit.target_item_id;

        return (
          <InsertedTextOverlayRow
            key={edit.edit_id}
            edit={edit}
            canvasRect={getCanvasRect(edit.rect)}
            displayScale={textDisplayScale}
            isSelected={isSelected}
            isEditing={
              insertedTextInteractive &&
              isSelected &&
              tempFields.editingInsertedTextId === edit.edit_id
            }
            interactive={insertedTextInteractive}
            moveEnabled={insertedTextMoveEnabled}
            onStartMove={handleInsertedTextMoveStart}
            onStartResize={handleInsertedTextResizeStart}
            highlightFields={highlightFields}
            isPreview={isPreview}
            documentKey={tempFields.currentDocument?.base64File}
            setCurrentProcessingFile={setCurrentProcessingFile}
            pushHistory={undoRedoStats?.pushHistory}
          />
        );
      })}

      <Dialog
        open={openEditBlockDialog}
        onClose={handleCloseEditBlockDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Assignment</DialogTitle>
        <DialogContent>
          <Stack gap={1}>
            {currentProcessingFile?.document?.tempFields?.formFields
              ?.filter(
                (f) =>
                  f?.block_id === selectedBlock &&
                  !f?.name?.startsWith("block__"),
              )
              ?.map((field) => {
                const isFieldDeleted = deletedFields?.includes(field?.name);

                return (
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    key={field?.name}
                  >
                    <Typography>{field?.field_label}</Typography>
                    <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (isFieldDeleted) {
                            setDeletedFields(
                              deletedFields?.filter((f) => f !== field?.name),
                            );
                          } else {
                            setDeletedFields((prev) => [...prev, field?.name]);
                          }
                        }}
                        sx={{
                          border: "1px solid #d0d7de",
                          borderRadius: 1.5,
                          backgroundColor: "transparent",
                          p: "4px !important",
                        }}
                      >
                        {isFieldDeleted ? (
                          <AttachFileIcon
                            sx={{
                              fontSize: "1rem",
                              transform: "rotate(45deg)",
                            }}
                          />
                        ) : (
                          <LinkOffIcon sx={{ fontSize: "1rem" }} />
                        )}
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button
            onClick={() => handleEditConfirm(true)}
            variant="contained"
            sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
          >
            Unassign All
          </Button>
          <Box display={"flex"} alignItems={"center"} gap={1}>
            <Button
              onClick={handleCloseEditBlockDialog}
              variant="text"
              sx={{ textTransform: "none", color: "text.secondary" }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleEditConfirm()}
              variant="contained"
              sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
            >
              Update
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(FormFieldOverlayLayer);
